import { useMemo } from 'react';
import { ahuTunnelAddress, type IAhuUnit, type IMasterNameRow } from 'models';
import { formatNumberWithCommaSeparator } from '../../ahuUtils';
import { useAhuSections } from '../../hooks/useAhuSections';

interface AhuInfoBarProps {
  unit: IAhuUnit;
  cmsProducts: IMasterNameRow[] | undefined;
  weatherRatings: IMasterNameRow[] | undefined;
  /** EDIT AHU → /ahu/detail/{id} same tab (parity 139). */
  onOpenAhu: (id: number) => void;
}

/**
 * Right info bar (`ahus/tmpl/ahu_info_bar.tmpl` rendered into #ahus_info-bar
 * by viewProjectAhuUnit, B1.4 / parity 141-143): "Properties & Performance"
 * rows (exact labels/order), the "Sections" pane fed by the tunnels→sections
 * pipeline, and the EDIT AHU button. The `.full-ahu-preloader` stays active
 * until the sections load.
 *
 * [NEG] honored (parity 144): MCA / MOP rows (class `hide`), the
 * `fn['get.all.cms.data']` no-op and the target-less bindFormatter pass are
 * not rebuilt. The legacy per-click static-versions GET (B1.9) is dropped
 * (intentional network delta, OQ B-2).
 */
const AhuInfoBar = ({ unit, cmsProducts, weatherRatings, onOpenAhu }: AhuInfoBarProps) => {
  const isHybrid = unit.is_hybrid ?? false;
  const { tunnels, sections } = useAhuSections(unit.id, isHybrid);

  const productName = useMemo(
    () => cmsProducts?.find((p) => p.id === unit.product_id)?.name ?? '',
    [cmsProducts, unit.product_id]
  );
  const weatherRatingName = useMemo(
    () => weatherRatings?.find((w) => w.id === unit.weather_rating_id)?.name ?? '',
    [weatherRatings, unit.weather_rating_id]
  );
  const certification = useMemo(
    () => (unit.certification ?? []).map((c) => c.name ?? '').join(', '),
    [unit.certification]
  );
  const saVolumeMax =
    unit.sa_volume_max != null && unit.sa_volume_max !== ''
      ? formatNumberWithCommaSeparator(unit.sa_volume_max)
      : '';

  // Multi-tunnel "Tunnel {i} Dimensions" rows (parity 142): only when more
  // than one tunnel; the shared tunnel_configuration prefix is stripped.
  const tunnelDims = useMemo(() => {
    if (!tunnels || tunnels.length <= 1) return [];
    const cfg = unit.tunnel_configuration ?? '';
    return tunnels.map((t) => ({
      index: ahuTunnelAddress(t, isHybrid),
      dimensions: cfg ? (t.dimensions ?? '').replace(cfg, '') : t.dimensions ?? '',
    }));
  }, [tunnels, unit.tunnel_configuration, isHybrid]);

  const infoRow = (label: string, value: React.ReactNode, extra?: string) => (
    <div className="ahus_info row">
      <div className="ahus_info_label col-xs-4">{label}</div>
      <div className={`ahus_info_data col-xs-8${extra ? ` ${extra}` : ''}`}>{value}</div>
    </div>
  );

  return (
    <div className="row full-height">
      <div
        className={`full-ahu-preloader preloader dotted-preloader${
          sections === null ? ' active' : ''
        }`}
      />
      <div className="col-xs-12 col-md-7 col-lg-7 full-height padding-right-0 width-lg-60">
        <div className="row">
          <div className="col-xs-12 full-height col-md-12 right-column-scrollable padding-right-0">
            <div className="col-xs-12">
              <div className="row block-header">
                <div className="col-xs-12 ">
                  <div className="row">
                    <div className="col-xs-1 account-icon">
                      <i className="icon-baseline-description-24px properties-icon" />
                    </div>
                    <div className="col-xs-10 block-header-title">
                      <span className="block-title ">Properties & Performance</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row scrollable">
                <div className="col-xs-12">
                  <div className="">
                    {infoRow(
                      'Tag',
                      unit.static_version ? unit.static_version_name : unit.tag,
                      'too_long_text'
                    )}
                    {infoRow(
                      'Supply Airflow',
                      <>
                        {saVolumeMax}
                        {saVolumeMax ? <span className=""> CFM</span> : null}
                        {unit.sa_volume_control?.name
                          ? ` / ${unit.sa_volume_control.name}`
                          : ''}
                      </>
                    )}
                    {infoRow('Product', productName)}
                    {infoRow('Qty', unit.qty as React.ReactNode)}
                    {infoRow('Model', unit.model)}
                    {infoRow('Serial Number', unit.serial)}
                    {infoRow('AHU Dimensions', unit.dimensions)}
                    <div className="ahus_info row" id="ahu-dimensions">
                      <div className="ahus_info_label col-xs-4">Tunnel Wide x High</div>
                      <div className="ahus_info_data col-xs-8">
                        {unit.tunnel_configuration}
                      </div>
                    </div>
                    {tunnelDims.map((t) => (
                      <div className="ahus_info row tunnels_dim" key={`tunnel_${t.index}`}>
                        <div className="ahus_info_label col-xs-4">
                          Tunnel {t.index} Dimensions
                        </div>
                        <div className="ahus_info_data col-xs-8">{t.dimensions}</div>
                      </div>
                    ))}
                    {infoRow('Air System', unit.air_system?.name)}
                    {unit.ahu_main_voltage
                      ? infoRow('Voltage', unit.ahu_main_voltage)
                      : null}
                    {infoRow('Location', unit.location)}
                    {infoRow('Weather Rating', weatherRatingName)}
                    {infoRow('Service', unit.service)}
                    {infoRow('Certification', certification)}
                    <div className="ahus_info row">
                      <div className="ahus_info_label col-xs-4">Remarks</div>
                      <div
                        className=" ahus_info_data col-xs-8 remarks  ka-has-tooltip remarks-tooltip"
                        tooltip={unit.remarks ?? ''}
                      >
                        <div className="too_long_text remarks">{unit.remarks}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row" id="last-edited">
              <div className="col-xs-12">
                <div className="">
                  <div className="ahus_info row">
                    <div className="ahus_info_label col-xs-4">Last edited</div>
                    <div className="ahus_info_data col-xs-8">
                      {unit.last_edited ? <span> {unit.last_edited}</span> : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr />
      <div className="col-xs-12 full-height col-md-5 col-lg-5 right-column-scrollable padding-right-0 width-lg-40">
        <div className="col-xs-12 full-height">
          <div className="row block-header">
            <div className="col-xs-12 " style={{ paddingLeft: 7 }}>
              <div className="">
                <div className="col-xs-1 account-icon">
                  <i className="icon-baseline-description-24px sections-icon" />
                </div>
                <div className="col-xs-7 block-header-title">
                  <span className="block-title ">Sections</span>
                </div>
              </div>
            </div>
          </div>
          <div className="row scrollable sectioinfo">
            <div className="col-xs-12 padding-right-0">
              <div className="" id="sectioInfo">
                {(sections ?? []).map((s, i) => (
                  <div
                    className="col-xs-12 ahus_info padding-right-0"
                    key={`${s.tunnelId}_${s.name}_${i}`}
                  >
                    {s.index} &nbsp; {s.name} {s.tag}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-xs-2 block-ctr-button-edit col-xs-offset-10 ">
        <button
          id={String(unit.id)}
          data-id={unit.id}
          className="btn-edit btn edit edit-ahu"
          onClick={() => onOpenAhu(unit.id)}
        >
          EDIT AHU
        </button>
      </div>
    </div>
  );
};

export default AhuInfoBar;
